import React, { useMemo } from 'react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/Components/shadcn';
import { cn } from '@/lib/utils';
import { MenuViewItem, MenuViews } from '@/Models/ProfileMenu';
import ProfileMenuHeader from './ProfileMenuHeader';
import ProfileMenuItem from './ProfileMenuItem';

interface ProfileMenuProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    trigger: React.ReactElement;
    tooltipContent?: React.ReactNode;
    menuViewItems: MenuViewItem[];
    currentView: MenuViews;
}

const ProfileMenu = ({
    open,
    onOpenChange,
    trigger,
    tooltipContent,
    menuViewItems,
    currentView,
}: ProfileMenuProps): React.JSX.Element => {
    const isMainView = currentView === MenuViews.Main;
    const currentMenuItems = useMemo(
        () => menuViewItems.find((item) => item.viewId === currentView)?.menuItems ?? [],
        [menuViewItems, currentView],
    );

    return (
        <TooltipProvider>
            <Popover open={open} onOpenChange={onOpenChange}>
                <Tooltip open={!tooltipContent ? false : undefined}>
                    <PopoverTrigger asChild>
                        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                    </PopoverTrigger>
                    <TooltipContent>{tooltipContent}</TooltipContent>
                </Tooltip>
                <PopoverContent
                    align="end"
                    side="bottom"
                    sideOffset={4}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={cn('flex max-h-90 w-54 max-w-54 flex-col gap-0 overflow-hidden p-0')}
                >
                    <div
                        key={currentView}
                        className={cn(
                            'flex min-h-0 flex-1 flex-col py-1',
                            'animate-in fade-in-0 duration-200 ease-out',
                            currentView === MenuViews.Companies ? 'slide-in-from-right-3' : 'slide-in-from-left-2',
                        )}
                    >
                        {isMainView && <ProfileMenuHeader />}
                        <div
                            className={cn(
                                'relative min-h-0 flex-1 gap-1 px-1',
                                !isMainView && 'max-h-70 overflow-y-auto overscroll-contain',
                            )}
                        >
                            {currentMenuItems.map(({ onClick, menuItemLabel, id, labelClassName, ...props }) => (
                                <ProfileMenuItem
                                    key={id ?? menuItemLabel}
                                    id={id}
                                    menuItemLabel={menuItemLabel}
                                    labelClassName={labelClassName}
                                    onClick={onClick}
                                    {...props}
                                />
                            ))}
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </TooltipProvider>
    );
};

export default ProfileMenu;
